import { AmountInterface } from '../../../../layout/interfaces/amount-interface';
import { RateInterface } from '../../../../layout/interfaces/rate-interface';
import { LegacyAddonDto } from '../../generated';
import { Person } from './person';
import { Product } from './product';

export class Addon implements LegacyAddonDto {
  id: number;
  name: { [key: string]: string };
  nameTranslationKey: string;
  descriptionForAcceptedLanguages: { [key: string]: string | undefined };
  freeChild: boolean;
  isAvailableInShop: boolean;
  classname: string;
  definitionId: number;
  price: { [key: string]: number | undefined };
  priceRelative: number;
  priceCurrencyCode: string;
  isSelected = false;

  constructor(addon: LegacyAddonDto) {
    Object.keys(addon).forEach(key => {
      this[key] = addon[key];
    });
  }

  /**
   * gives you this addons 'online-to-pay-on-our-site' price with discount based on its base price.
   * If you want the 'price-you'd-have-to-pay-in-the-store' price, use getShopPrice()
   * @param days the number of days you want to rent this item (the base price depends on it)
   * @param person the person you want the price for (has the discount)
   * @param product the product this addon is for (used to find the discount in the person)
   * @param rates the exchange rate for EUR to return to you an AmountInterface
   * @param forCurrency the currency you want this price to be calculated for (from EUR -> forCurrency)
   * @returns an AmountInterface containing the amount: discountPrice, rates: eurobasedRates
   */
  getOnlinePrice(
    days: number,
    person: Person,
    product: Product,
    rates: RateInterface,
    forCurrency: string
  ): AmountInterface {
    // skip any calculations if addon is for any reason free of charge
    if (person.discounts?.isFree && this.freeChild) {
      return { amount: 0, currency: { code: forCurrency, rates: {} } };
    }
    let reducedPrice = 0;

    if (this.priceRelative > 0 && this.classname === 'insurance') {
      // product price
      reducedPrice += Math.round(+product.price[days] * rates.rates[forCurrency]);

      // + sum of addons that can be insured
      product.addons
        .filter(
          (
            addon // only Helmet and Boots affect the insurance price
          ) => addon.definitionId === 1 || addon.definitionId === 2
        )
        .filter(addon => addon.isSelected && addon.isAvailableInShop)
        .forEach(addon => {
          reducedPrice += Math.round(+addon.price[days] * rates.rates[forCurrency]);
        });

      // calculate relative price and apply discount for the insurance for this person
      reducedPrice = Math.round(
        Math.round(reducedPrice * this.priceRelative) * (1 - product.getDiscount(person))
      );
    } else {
      reducedPrice += Math.round(
        Math.round(+this.price[days] * rates.rates[forCurrency]) * // exchange EUR to target, round
          (1 - product.getDiscount(person)) // apply discount, round
      );
    }

    return { amount: reducedPrice, currency: { code: forCurrency, rates: {} } };
  }

  /**
   * returns the 'this-is-the-price-you'd-pay-in-the-store' price (the bad one to compare to our online price)
   * @param days the number of days you want to rent this addon
   * @param product the product to calculate a relative price if applicable
   * @param rates the exchange rates to get amount in target currency
   * @param forCurrency the currency you want to exchange to
   * @returns AmountInterface containing the amount in given currency (no rates)
   */
  getShopPrice(days: number, product: Product, rates: RateInterface, forCurrency: string): AmountInterface {
    let basePrice = Math.round(+this.price[days] * rates.rates[forCurrency]);

    if (this.priceRelative > 0 && this.classname === 'insurance') {
      // product price
      basePrice = Math.round(+product.price[days] * rates.rates[forCurrency]);
      // sum up addons
      product.addons
        .filter(
          (
            addon // only Helmet and Boots affect the insurance price
          ) => addon.definitionId === 1 || addon.definitionId === 2
        )
        .filter(addon => addon.isSelected && addon.isAvailableInShop)
        .forEach(addon => {
          basePrice += Math.round(addon.price[days] * rates.rates[forCurrency]);
        });

      basePrice = Math.round(basePrice * this.priceRelative);
    }
    // product in use here for relative priced items
    return {
      amount: basePrice,
      currency: { code: forCurrency, rates: {} }
    };
  }
}
